import { useCallback, useState } from "react";
import { Badge, Button, Card, Grid, Group, Select, Stack, Text, TextInput } from "@mantine/core";
import { IconBarcode, IconCamera } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import type { QualityScore } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FoodDetailPanel } from "../components/food";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { ClientSettingsHint } from "../components/ModelPicker";
import { SAMPLE_BARCODES } from "../lib/samples";

const SAMPLES = SAMPLE_BARCODES;

export function BarcodePage() {
  const [code, setCode] = useState("");
  const [minQuality, setMinQuality] = useState<QualityScore | null>(null);
  const [scanning, setScanning] = useState(false);
  const lookup = useMutation({ mutationFn: (c: string) => passio.foods.byProductCode(c, { minQualityScore: minQuality ?? undefined }) });
  const { mutate } = lookup;
  const run = useCallback(
    (c: string) => {
      const clean = c.replace(/\D/g, "");
      if (!clean) return;
      setCode(clean);
      mutate(clean);
    },
    [mutate],
  );
  const onDetected = useCallback((c: string) => run(c), [run]);
  const closeScanner = useCallback(() => setScanning(false), []);

  return (
    <RequireFeature feature="lookup">
      <PageHeader eyebrow="GET /napi/food/productCode/{productCode}" title="Barcode lookup" description="UPC / EAN to a packaged food with full nutrition, ingredients and allergens. Scan with your camera or type the digits." actions={<ClientSettingsHint />} />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Card>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                run(code);
              }}
            >
              <Stack gap="sm">
                <TextInput label="Barcode" placeholder="e.g. 818290014474" value={code} onChange={(e) => setCode(e.currentTarget.value)} leftSection={<IconBarcode size={18} />} inputMode="numeric" />
                <Select label="Minimum quality" placeholder="Any" clearable data={["Low", "Medium", "High"]} value={minQuality} onChange={(v) => setMinQuality(v as QualityScore | null)} />
                <Group grow>
                  <Button type="submit" loading={lookup.isPending}>
                    Look up
                  </Button>
                  <Button variant="light" color="violet" leftSection={<IconCamera size={16} />} onClick={() => setScanning(true)}>
                    Scan
                  </Button>
                </Group>
                <Text fz="xs" c="dimmed" mt="xs">
                  Try one:
                </Text>
                <Group gap={6}>
                  {SAMPLES.map((s) => (
                    <Badge key={s.code} variant="outline" color="gray" tt="none" style={{ cursor: "pointer" }} onClick={() => run(s.code)}>
                      {s.label}
                    </Badge>
                  ))}
                </Group>
              </Stack>
            </form>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 8 }}>
          {lookup.error && <ErrorNotice error={lookup.error} onRetry={() => run(code)} />}
          {lookup.data && (
            <Card>
              <ResultHeader title={`Barcode ${code}`} meta={lookup.data.meta} feedback={<FeedbackButtons requestId={lookup.data.meta.requestId} itemRef={code} />} />
              <FoodDetailPanel key={lookup.data.record.id} data={lookup.data.data} />
            </Card>
          )}
          {!lookup.data && !lookup.error && <EmptyState title={lookup.isPending ? "Looking up…" : "No product yet"}>Scan or enter a barcode to see the product.</EmptyState>}
        </Grid.Col>
      </Grid>
      <BarcodeScanner opened={scanning} onClose={closeScanner} onDetected={onDetected} />
    </RequireFeature>
  );
}
